import { ReactNode } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, FileText, MessageSquare, Image, LogOut, ExternalLink, Mail, FileCode, Brain, ThumbsUp, TrendingUp } from "lucide-react";
import SEO from "@/components/SEO";

interface AdminLayoutProps {
  children: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  

  const handleSignOut = async () => {
    await signOut();
    navigate("/admin/login");
  };

  const navItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/posts", label: "Posts", icon: FileText },
    { path: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
    { path: "/admin/follow-ups", label: "Follow-ups", icon: Mail },
    { path: "/admin/email-templates", label: "Email Templates", icon: FileCode },
    { path: "/admin/media", label: "Media", icon: Image },
    { path: "/admin/ai-knowledge", label: "AI Knowledge", icon: Brain },
    { path: "/admin/feedback", label: "AI Feedback", icon: ThumbsUp },
    { path: "/admin/seo", label: "SEO", icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Admin | Rick's Black Car Service"
        description="Private administration area for Rick's Black Car Service."
        canonicalPath={location.pathname}
        noindex
        includeBreadcrumbs={false}
      />

      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="w-64 border-r bg-card min-h-screen p-4 flex flex-col">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-foreground px-3 py-2">Rick's Blog CMS</h2>
          </div>
          
          <nav className="space-y-2 flex-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant={isActive ? "secondary" : "ghost"}
                    className="w-full justify-start"
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}
          </nav>

          {/* Sidebar Footer Actions */}
          <div className="mt-auto pt-4 border-t space-y-2">
            <Link to="/" target="_blank">
              <Button variant="ghost" className="w-full justify-start">
                <ExternalLink className="h-4 w-4 mr-2" />
                View Site
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleSignOut}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
